/* ── BRIEF 129 §6 — OPENING THE DOCUMENT A TIME ENTRY WAS BILLED FROM ───────
 *
 * §6 asks for this opener "beside the email one, in the same file or a sibling —
 * say which and why". IT IS A SIBLING, and the reason is what each file is
 * ABOUT rather than how long either is.
 *
 * `emailLink.ts` is a file about a URI SCHEME. Its header declares it
 * ("`message:<internetMessageId>`, the RFC-2392-shaped URI Apple Mail and
 * Outlook put on the pasteboard"), and every one of its exports is that scheme:
 * `emailDocUrl` writes it, `isEmailDocUrl` recognises it, `emailIdFromDocUrl`
 * parses it, `openEmailDocHref` takes it. All four exist because an email is
 * stored as a `{ label, url }` DOCUMENT ROW and the url must be a string.
 *
 * A document's provenance is none of that. `TimeEntry.docItemId` is a bare
 * Graph drive item id in a column, with no row, no scheme, no url to write or
 * parse — and it is meaningless without the MATTER, because the drive it lives
 * in is resolved from that matter's own folder. Putting a matter-scoped id
 * resolver inside a file whose header declares the `message:` scheme would make
 * that header false, and the four scheme functions would sit beside one that
 * has no scheme to work with.
 *
 * WHAT THE TWO GENUINELY SHARE is a shape, not an implementation: "resolve an
 * opaque provenance id through a route, cache the answer for the session, open
 * it, and name the reason when it cannot be opened". That shape is three lines
 * each — a Map, a toast, one `openDocHref` call — and it is OBSERVED here
 * rather than abstracted, because the two differ in the route, in the arguments
 * that route needs, in the cache key and in what a failure means. A parameter
 * for each of those is a second implementation wearing one name.
 *
 * ── WHAT IT DOES NOT DO ────────────────────────────────────────────────────
 *
 * `openDocHref` is UNTOUCHED, exactly as 126 §9 left it: it still opens
 * `http(s)` and bare `www.` only. This resolves an id to a real `webUrl` and
 * hands THAT to it, so nothing new is ever opened.
 */
import { api } from "../api";
import { openDocHref } from "../components/SharedUI";
import type { TFn } from "./taxonomyTypes";

/** The two matter kinds, spelled as `api.ts` spells them. */
export type DocItemMatter = { caseId?: number | string | null; ccId?: number | string | null };

/* Cached for the SESSION, and keyed by MATTER AND ITEM, not by item alone: the
   same id addressed against a different matter is a different question (a
   different drive resolution, a different gate), and one answer must not stand
   in for the other. Nothing is persisted — a `webUrl` rots, which is why 125 §4
   ruled that the ID is what gets stored and never the link. */
const resolved = new Map<string, { name: string | null; webUrl: string }>();

function keyOf(m: DocItemMatter, itemId: string) {
  return `${m.ccId != null ? `cc:${m.ccId}` : `ca:${m.caseId}`}|${itemId}`;
}

/* ── BRIEF 130 §2 — THIS FILE WAS INSPECTED, AND THE ANSWER IS "NOT THAT" ───
 *
 * §2: "if it collapses several server reasons into one sentence the way `say()`
 * does, it gets the same treatment; if it does not, say so and change nothing."
 *
 * [MEASURED] IT DOES NOT. `GET …/documents/item-link/:itemId`
 * (`documentBilling.js:376-403`) answers 200 in exactly ONE shape and THROWS
 * every distinct failure with its own status and its own registered code — 400
 * `generic.bad_request`, 404 `m365.folder_not_found`, 409 `m365.not_connected`,
 * 502 `m365.graph_error`, 404 `generic.not_found`. All five reach the `catch`
 * below, where each already arrives as its own sentence through `api()`'s error
 * envelope. `say()` therefore has ONE non-throwing cause — a 200 whose `webUrl`
 * is null — and one cause needs one sentence. The email side's defect is
 * structurally absent here, and the two files are NOT made symmetrical for its
 * own sake.
 *
 * ── WHAT THE INSPECTION DID FIND, IN THE SAME LINE ────────────────────────
 *
 * The second argument is called `reason` and was rendered as ` (${reason})`,
 * and the one call site that passes anything passes **`r.name` — the
 * DOCUMENT'S FILENAME**. So a document SharePoint gave no link for said "This
 * document cannot be opened from here (contract.pdf)": a filename in the
 * parenthesis a reader has been trained to read as a cause. The route's header
 * is right that the name is returned so the refusal "can say WHICH document" —
 * the intent was sound and the rendering made it look like an error code.
 *
 * So the name goes INTO the sentence and the parenthesis goes away. It is the
 * same rule as the email side's — a sentence, not a value bolted on — arrived
 * at from a different defect, which is why the two texts differ.
 */
function say(t: TFn, name: string | null) {
  const n = String(name || "").trim();
  window.lxToast?.(
    n
      ? t("docLink.unavailable_named", {
          name: n,
          fallback: `“${n}” cannot be opened from here — SharePoint gives no link for it.`,
        })
      : t("docLink.unavailable", {
          fallback: "This document cannot be opened from here",
        }),
    "warning",
  );
}

/**
 * Open the document a time entry was billed from. Returns true when a tab was
 * opened.
 *
 * Never throws: it is called from an `onClick`, and a rejection there is the
 * swallowed-throw shape `no-silent-catch` exists for.
 */
export async function openDocItemHref(
  matter: DocItemMatter,
  itemId: unknown,
  t: TFn,
): Promise<boolean> {
  const id = String(itemId || "").trim();
  if (!id || (matter.caseId == null && matter.ccId == null)) {
    say(t, null);
    return false;
  }
  const hit = resolved.get(keyOf(matter, id));
  if (hit) return openDocHref(hit.webUrl);
  const base =
    matter.ccId != null
      ? `/collective-cases/${matter.ccId}/documents`
      : `/cases/${matter.caseId}/documents`;
  try {
    const r = (await api.get(`${base}/item-link/${encodeURIComponent(id)}`)) as {
      webUrl?: string | null;
      name?: string | null;
    } | null;
    const link = r && r.webUrl;
    if (link) {
      resolved.set(keyOf(matter, id), { name: (r && r.name) || null, webUrl: link });
      return openDocHref(link);
    }
    /* The document is there and SharePoint gave no browser link for it. Named,
       not collapsed into "not found" — the 124 ruling, kept. 130 §2: the name
       is now named IN the sentence rather than parenthesised after it. */
    say(t, (r && r.name) || null);
    return false;
  } catch (e) {
    /* NOT swallowed (`no-silent-catch`): the route answers 502 for "I could not
       ask Graph", 404 for "it is not there" and 409 for "M365 is not
       connected", and each carries its own registered sentence through
       `api()`'s error envelope. Collapsing them here would undo the reason the
       route distinguishes them. */
    window.lxToast?.(
      "⚠ " +
        ((e as Error)?.message ||
          t("docLink.unavailable", {
            fallback: "This document cannot be opened from here",
          })),
      "warning",
    );
    return false;
  }
}
